import { PropsWithChildren, useEffect } from 'react';

import {
  DevHubDynamicIcon,
  devHubDynamicSvgIcon,
  DevHubLink,
  devhubMakeStyles,
} from '@devxdiscover/devhub-ui';
import LogoFull from './LogoFull';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  useSidebarOpenState,
} from '@backstage/core-components';
import { Theme, ThemeProvider } from '@material-ui/core/styles';
import { lightTheme } from '@backstage/theme';
import { configApiRef, useAnalytics, useApi } from '@backstage/core-plugin-api';
import { ConfigFeatureCheck } from '../../custom/util/ConfigFeatureCheck';
import { useNavigate } from 'react-router';
import {
  CatalogServiceFactory,
  ConfigHelper,
  Feature,
} from '@devxdiscover/devhub-common';
import ChatBot from './ChatBot';
import FlowHubIcon from '../../custom/components/flowhub/FlowHubIcon';
import NovaIcon from '../../custom/components/agent/NovaIcon';

const useSidebarLogoStyles = devhubMakeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <DevHubLink
        component={NavLink}
        to="/"
        underline="none"
        className={classes.link}
        aria-label="Home"
      >
        {isOpen ? <LogoFull height="110px" /> : <LogoFull height="50px" />}
      </DevHubLink>
    </div>
  );
};

/**
 * The root component of the app.
 * @param children - The children to render in the app.
 * @constructor
 */
export const Root = ({ children }: PropsWithChildren<{}>) => {
  const themeId = 'home';
  const analytics = useAnalytics();
  ConfigHelper.init(useApi(configApiRef));
  CatalogServiceFactory.init(useApi(configApiRef));

  const navigate = useNavigate();
  const location = useLocation();

  // Intercept all clicks on anchor tags and use React Router to navigate to
  // avoid full page reloads (and discard cached values and slow down the app)
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const anchor = target.closest('a');

      if (anchor) {
        const to = anchor.getAttribute('href');
        if (to && to.startsWith('/')) {
          event.preventDefault();

          // Check if the current location matches the target location
          if (location.pathname !== to) {
            navigate(to);
          }
        }
      }
    };

    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [navigate, location]);

  const handleSidebarItemClick = (label: string, route: string) => {
    analytics.captureEvent('sidebar-item-click', label.toLowerCase(), {
      attributes: {
        label,
        route,
      },
    });
  };

  return (
    <ThemeProvider
      theme={(baseTheme: Theme) => ({
        ...baseTheme,
        page: lightTheme.getPageTheme({ themeId }),
      })}
    >
      <SidebarPage>
        <Sidebar>
          <SidebarLogo />
          <ConfigFeatureCheck feature={Feature.SIDEBAR_SEARCH}>
            <SidebarGroup
              label="Search"
              icon={<DevHubDynamicIcon iconName="Search" />}
              to="/search"
            >
              <SidebarSearchModal />
            </SidebarGroup>
          </ConfigFeatureCheck>
          <ConfigFeatureCheck feature={Feature.NOVA}>
            <SidebarItem
              icon={() => <NovaIcon />}
              to="agent"
              text="Nova"
              style={{ textDecoration: 'none' }}
              onClick={() => handleSidebarItemClick('Nova', 'agent')}
            />
          </ConfigFeatureCheck>
          <ConfigFeatureCheck feature={Feature.ACTION_CENTER}>
            <SidebarItem
              icon={devHubDynamicSvgIcon('AlarmOn')}
              to="action-center"
              text="Action Center"
              style={{ textDecoration: 'none' }}
            />
          </ConfigFeatureCheck>
          <ConfigFeatureCheck feature={Feature.NOTIFICATIONS}>
            <NotificationsSidebarItem />
          </ConfigFeatureCheck>
          <SidebarDivider />
          <SidebarGroup
            label="Menu"
            icon={<DevHubDynamicIcon iconName="Menu" />}
          >
            {/* Global nav, not org-specific */}
            <ConfigFeatureCheck feature={Feature.ACTION_CENTER}>
              <SidebarItem
                icon={devHubDynamicSvgIcon('Home')}
                to="/"
                text="Home"
                style={{ textDecoration: 'none' }}
              />
            </ConfigFeatureCheck>
            <ConfigFeatureCheck feature={Feature.APPLICATIONS}>
              <SidebarItem
                icon={devHubDynamicSvgIcon('Apps')}
                to="my-apps"
                text="Applications"
                style={{ textDecoration: 'none' }}
              />
            </ConfigFeatureCheck>
            <ConfigFeatureCheck feature={Feature.API_PORTAL}>
              <SidebarItem
                icon={devHubDynamicSvgIcon('Assignment')}
                to="api-portals"
                text="API Portals"
                style={{ textDecoration: 'none' }}
              />
            </ConfigFeatureCheck>
            <ConfigFeatureCheck feature={Feature.SOLUTIONS}>
              <SidebarItem
                icon={devHubDynamicSvgIcon('QuestionAnswer')}
                to="solutions"
                text="Solutions"
                style={{ textDecoration: 'none' }}
                onClick={() => handleSidebarItemClick('Solutions', 'solutions')}
              />
            </ConfigFeatureCheck>
            <ConfigFeatureCheck feature={Feature.COMMUNITY}>
              <SidebarItem
                icon={devHubDynamicSvgIcon('People')}
                to="community/default/component/devhub-community"
                text="Community"
                style={{ textDecoration: 'none' }}
              />
            </ConfigFeatureCheck>
            <ConfigFeatureCheck feature={Feature.FLOWHUB}>
              <SidebarItem
                icon={() => <FlowHubIcon />}
                to="flowhub"
                text="FLOW Hub"
                style={{ textDecoration: 'none' }}
              />
            </ConfigFeatureCheck>
            <ConfigFeatureCheck feature={Feature.CONNECTIONS}>
              <SidebarItem
                icon={devHubDynamicSvgIcon('AccountTree')}
                to="connections"
                text="Connections"
                style={{ textDecoration: 'none' }}
              />
            </ConfigFeatureCheck>
            <ConfigFeatureCheck feature={Feature.MARKETPLACE}>
              <SidebarItem
                icon={devHubDynamicSvgIcon('Store')}
                to="marketplace"
                text="Marketplace"
                style={{ textDecoration: 'none' }}
              />
            </ConfigFeatureCheck>
            <ConfigFeatureCheck feature={Feature.CATALOG}>
              <SidebarItem
                icon={devHubDynamicSvgIcon('Category')}
                to="catalog-search"
                text="Catalog"
                style={{ textDecoration: 'none' }}
              />
            </ConfigFeatureCheck>
            <ConfigFeatureCheck feature={Feature.DOCUMENTATION}>
              <SidebarItem
                icon={devHubDynamicSvgIcon('LibraryBooks')}
                to="documentation"
                text="Docs"
                style={{ textDecoration: 'none' }}
              />
            </ConfigFeatureCheck>
            <ConfigFeatureCheck feature={Feature.CHEATSHEET}>
              <SidebarItem
                icon={devHubDynamicSvgIcon('FormatListBulleted')}
                to="cheat-sheet/default/component/devhub-service-directory"
                text="CheatSheet"
                style={{ textDecoration: 'none' }}
              />
              <ConfigFeatureCheck feature={Feature.DPX_WORKFLOW_SIDEBAR}>
                <SidebarItem
                  icon={devHubDynamicSvgIcon('Security')}
                  to="dpx-workflow"
                  text="DPX Workflow"
                  style={{ textDecoration: 'none' }}
                />
              </ConfigFeatureCheck>
            </ConfigFeatureCheck>
            <ConfigFeatureCheck feature={Feature.ONBOARDING}>
              <SidebarItem
                icon={devHubDynamicSvgIcon('ControlPoint')}
                to="onboarding"
                text="Onboarding"
                style={{ textDecoration: 'none' }}
              />
            </ConfigFeatureCheck>

            {/* End global nav */}
            <SidebarDivider />
          </SidebarGroup>
          <SidebarSpace />
          <SidebarDivider />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
            style={{ textDecoration: 'none' }}
          >
            <SidebarSettings />
          </SidebarGroup>
        </Sidebar>
        {children}
      </SidebarPage>
      <ChatBot />
    </ThemeProvider>
  );
};
